// die-cut closing — bridge sub-border-scale background pockets in a stroked
// silhouette, the way a physical die-cut would.
//
// THE DEFECT (owner report 2026-07-16, "pocket above the i"): at wide stroke
// widths the border ring around an i-dot and the border around the stem and
// neighboring letters approach and merge, and the background caught between
// them survives as either
//   (a) a fully ENCLOSED pocket (sticker border ~80 / outline ~40 at the
//       merge-onset window — flood-fill-verified in the 2026-07-16
//       classification), or
//   (b) a deep NARROW PINCH still open to the sky through a sub-border neck
//       (the matrix strokes: sticker 160-200, outline 90).
// Both shade as a dark crescent under any EDT-driven bevel (live fx bevel,
// baked emboss) and both are geometry a physical die-cut cannot produce: a
// cut line neither leaves sub-border backing-paper islands nor follows a
// crease narrower than its own cutting scale.
//
// THE OP: morphological CLOSE (dilate then erode) with a DISK structuring
// element on the rendered alpha, with every closed-in pixel INPAINTED from
// its nearest original ink pixel (a multi-source BFS wavefront copying
// straight RGB), so solid AND textured borders bridge without a seam. Two
// feather passes keep the result anti-aliased: bridged pixels exposed to
// remaining background get coverage-weighted alpha, and the original
// semi-transparent rim pixels that now sit INSIDE solid ink are composited
// over the inpaint instead of leaking background through a 1px ring.
//
// Disk, not square, and it matters (measured 2026-07-16): the die-cut border
// silhouette is a chain of ARCS (round joins), and a square SE's flat sides
// shave every shallow arc-to-arc scallop along the outer rim into 1-2px
// semi-opaque slivers — under the bevel those rendered as a dashed speckle
// line hugging the crown. A disk of radius r is the physical cutter: it
// rolls INTO any valley whose curvature is gentler than 1/r (crown scallops:
// untouched, bit-identical rim) and cannot enter a sub-radius pinch (the
// pocket: bridged). Both morphology halves are capped integer chamfer-5/7/11
// distance transforms (two sweeps each, ≈2% of exact Euclidean — the disk
// stays a disk): dilate = dist-to-ink ≤ r, erode = keep where
// dist-to-outside-the-dilation > r — O(px) at any radius, and a fraction of
// the exact EDT's cost (the parabola-envelope EDT measured ~20ms at 800×300
// on the dev VM; this runs per keystroke, so integers win).
//
// THE RADIUS LAW (calibrated 2026-07-16 against Arial fs80 rasters, see the
// changelog): r = min(reach · 0.4, 6 · unit), skipped entirely under 1.5
// device px, where reach = the stroke's outward spill (strokeWidth/2 for a
// centered fabric stroke, the dilation radius for baked/plate silhouettes)
// and unit = fontSize/100 in device px (the recipe-unit convention).
//   - pockets/creases: regime (a) pockets seal from r ≈ 3u; regime (b)
//     pinches round into open valleys no narrower than 2r — the bevel then
//     shades them as smooth die-cut valleys instead of dark creases;
//   - counters: any counter whose inscribed radius is ≥ 6u survives at EVERY
//     border width. Counter remnants thinner than the radius do close — at
//     that point they are genuinely sub-border-scale (a 200-width border has
//     already swallowed 'e'/'a'/'o' counters via the stroke's own inward
//     half; the closing only takes slivers a real cutter could never leave).
//   - the 0.4·reach term keeps small borders gentle: at sticker border 40
//     (reach 6u) the radius is 2.4u and every readable counter survives.
//
// Determinism: pure typed-array integer math — no canvas filters, no float
// accumulation across pixels — so the baked-emboss byte-identity guard can
// hold across runs (verified ×2 in probe-diecut).
//
// No deps. Imported by fxRender (live), embossRecipe (bake) and plateGeometry
// (3D plate); useCanvasEditor reaches it through fxRender's exported helper.

/** Alpha at or above this is "ink" for the closing mask. Semi pixels below it
 * (anti-aliased rims, shadow wash) are background and may be inpainted. */
const INK_ALPHA = 128;

/**
 * The calibrated radius law (see header). Returns 0 when closing would be
 * sub-pixel noise.
 * @param {number} reachPx outward stroke spill, device px.
 * @param {number} unitPx  device px per recipe unit (fontSize/100 · scale).
 */
export function diecutCloseRadiusPx(reachPx, unitPx) {
  const r = Math.min(reachPx * 0.4, 6 * unitPx);
  return r >= 1.5 ? Math.round(r) : 0;
}

// --- capped chamfer 5-7-11 distance transform -------------------------------
// Two-pass integer chamfer (weights 5 orthogonal / 7 diagonal / 11 knight —
// ≈2% of exact Euclidean, so the SE stays effectively a disk). Chosen over
// the exact F-H EDT for the SYNC budget: this runs per keystroke on the
// plain-path seam, and the parabola-envelope EDT in Float64 measured ~20ms at
// 800×300 on the dev VM where these integer sweeps measure a few ms. All
// distances are ×5 fixed-point and CAPPED just past the radius — everything
// farther is "far" and never compared.

/**
 * dist[i] = capped chamfer distance (×5) to the nearest seed. Seeds are
 * pixels where `seed[i]` is truthy (invert=false) or falsy (invert=true).
 */
function chamferDist(seed, W, H, cap, invert) {
  const N = W * H;
  const INF = cap + 12; // one knight-step of headroom; acts as "far"
  const d = new Int32Array(N);
  for (let i = 0; i < N; i++) d[i] = (invert ? !seed[i] : !!seed[i]) ? 0 : INF;
  const min2 = (a, b) => (a < b ? a : b);
  // forward: left/up neighborhood
  for (let y = 0; y < H; y++) {
    const row = y * W;
    const up = row - W;
    const up2 = row - 2 * W;
    for (let x = 0; x < W; x++) {
      const i = row + x;
      let v = d[i];
      if (v === 0) continue;
      if (x > 0) v = min2(v, d[i - 1] + 5);
      if (y > 0) {
        v = min2(v, d[up + x] + 5);
        if (x > 0) v = min2(v, d[up + x - 1] + 7);
        if (x < W - 1) v = min2(v, d[up + x + 1] + 7);
        if (x > 1) v = min2(v, d[up + x - 2] + 11);
        if (x < W - 2) v = min2(v, d[up + x + 2] + 11);
      }
      if (y > 1) {
        if (x > 0) v = min2(v, d[up2 + x - 1] + 11);
        if (x < W - 1) v = min2(v, d[up2 + x + 1] + 11);
      }
      d[i] = min2(v, INF);
    }
  }
  // backward: right/down neighborhood
  for (let y = H - 1; y >= 0; y--) {
    const row = y * W;
    const dn = row + W;
    const dn2 = row + 2 * W;
    for (let x = W - 1; x >= 0; x--) {
      const i = row + x;
      let v = d[i];
      if (v === 0) continue;
      if (x < W - 1) v = min2(v, d[i + 1] + 5);
      if (y < H - 1) {
        v = min2(v, d[dn + x] + 5);
        if (x < W - 1) v = min2(v, d[dn + x + 1] + 7);
        if (x > 0) v = min2(v, d[dn + x - 1] + 7);
        if (x < W - 2) v = min2(v, d[dn + x + 2] + 11);
        if (x > 1) v = min2(v, d[dn + x - 2] + 11);
      }
      if (y < H - 2) {
        if (x < W - 1) v = min2(v, d[dn2 + x + 1] + 11);
        if (x > 0) v = min2(v, d[dn2 + x - 1] + 11);
      }
      d[i] = min2(v, INF);
    }
  }
  return d;
}

/**
 * Morphological CLOSE of a 0/1 mask with a DISK SE of radius r (rolling-ball
 * closing — see module header for why not a square). Returns a NEW mask that
 * is a superset of the input by construction: dilation covers everything
 * within r of ink, so any non-dilated pixel is > r from ALL ink, so every
 * ink pixel survives the erosion threshold.
 */
export function closeMask(bin, W, H, r) {
  const N = W * H;
  const R = Math.round(r * 5); // chamfer fixed-point radius
  const d1 = chamferDist(bin, W, H, R, false); // distance to ink
  const dil = new Uint8Array(N);
  for (let i = 0; i < N; i++) dil[i] = d1[i] <= R ? 1 : 0;
  // The frame is never dilated: if ink+r overruns the image (tight plate
  // pads), the erode would otherwise have no outside seeds along that run
  // and the close would grow a rind down the raster edge. Callers' pockets
  // are interior, so pinning the frame open costs nothing.
  for (let x = 0; x < W; x++) { dil[x] = 0; dil[(H - 1) * W + x] = 0; }
  for (let y = 0; y < H; y++) { dil[y * W] = 0; dil[y * W + W - 1] = 0; }
  const d2 = chamferDist(dil, W, H, R, true); // distance to non-dilated
  const out = dil; // reuse — erode only ever clears bits
  for (let i = 0; i < N; i++) out[i] = d2[i] > R ? 1 : 0;
  return out;
}

/**
 * Mask-only die-cut closing for binary pipelines (the 3D plate): closes
 * `bin` IN PLACE and returns the number of bridged pixels. The erode half of
 * the close guarantees bin only ever GAINS pixels in sub-2r concavities —
 * straight edges and convex corners come back bit-identical.
 */
export function bridgeMaskPockets(bin, W, H, r) {
  if (!(r >= 1)) return 0;
  const closed = closeMask(bin, W, H, r);
  let bridged = 0;
  for (let i = 0; i < closed.length; i++) {
    if (closed[i] && !bin[i]) {
      bin[i] = 1;
      bridged++;
    }
  }
  return bridged;
}

/**
 * Full RGBA die-cut closing: close the alpha, inpaint bridged pixels from the
 * nearest original ink (straight RGB copy — works for any border paint), and
 * feather the two seam classes (see module header).
 *
 * @param {ImageData} img  mutated in place (straight, non-premultiplied RGBA).
 * @param {number} W
 * @param {number} H
 * @param {number} r  closing radius in px (already through the radius law).
 * @returns {number} bridged pixel count (0 = image untouched).
 */
export function bridgeDiecutPockets(img, W, H, r) {
  if (!(r >= 1)) return 0;
  const d = img.data;
  const N = W * H;
  const bin = new Uint8Array(N);
  let inkMinX = W;
  let inkMaxX = -1;
  let inkMinY = H;
  let inkMaxY = -1;
  for (let y = 0, i = 0, a = 3; y < H; y++) {
    for (let x = 0; x < W; x++, i++, a += 4) {
      if (d[a] >= INK_ALPHA) {
        bin[i] = 1;
        if (x < inkMinX) inkMinX = x;
        if (x > inkMaxX) inkMaxX = x;
        if (y < inkMinY) inkMinY = y;
        if (y > inkMaxY) inkMaxY = y;
      }
    }
  }
  if (inkMaxX < 0) return 0;

  // All morphology happens inside the ink bbox grown by r+1 — the (possibly
  // huge) empty spill margins around the shape cost nothing.
  const x0 = Math.max(0, inkMinX - r - 1);
  const y0 = Math.max(0, inkMinY - r - 1);
  const bw = Math.min(W - 1, inkMaxX + r + 1) - x0 + 1;
  const bh = Math.min(H - 1, inkMaxY + r + 1) - y0 + 1;
  const binC = new Uint8Array(bw * bh);
  for (let y = 0; y < bh; y++) {
    binC.set(bin.subarray((y0 + y) * W + x0, (y0 + y) * W + x0 + bw), y * bw);
  }

  // The closing is a geometry decision, not paint — above ~0.64Mpx (deep
  // zoom, print export) compute it on a block-OR downsampled mask with a
  // scaled radius and expand the verdicts back. OR keeps thin ink alive, the
  // upsampled mask still covers all ink (closed ⊇ bin blockwise), and the
  // sub-k mouth steps land under feather 1. Inpaint and feathers always run
  // at full resolution.
  const CLOSE_MAX_PX = 640 * 1024;
  let closed;
  if (bw * bh <= CLOSE_MAX_PX) {
    closed = closeMask(binC, bw, bh, r);
  } else {
    const k = Math.ceil(Math.sqrt((bw * bh) / CLOSE_MAX_PX));
    const ws = Math.ceil(bw / k);
    const hs = Math.ceil(bh / k);
    const binS = new Uint8Array(ws * hs);
    for (let y = 0; y < bh; y++) {
      const ys = ((y / k) | 0) * ws;
      const row = y * bw;
      for (let x = 0; x < bw; x++) {
        if (binC[row + x]) binS[ys + ((x / k) | 0)] = 1;
      }
    }
    const closedS = closeMask(binS, ws, hs, Math.max(1, Math.round(r / k)));
    closed = new Uint8Array(bw * bh);
    for (let y = 0; y < bh; y++) {
      const ys = ((y / k) | 0) * ws;
      const row = y * bw;
      for (let x = 0; x < bw; x++) {
        closed[row + x] = closedS[ys + ((x / k) | 0)];
      }
    }
  }

  // state (crop space): 0 = untouched · 1 = bridged, awaiting inpaint ·
  // 2 = inpainted
  const state = new Uint8Array(bw * bh);
  let bridged = 0;
  for (let i = 0; i < bw * bh; i++) {
    if (closed[i] && !binC[i]) {
      state[i] = 1;
      bridged++;
    }
  }
  if (!bridged) return 0;

  // crop index -> full-image pixel index
  const toFull = (ci) => (y0 + ((ci / bw) | 0)) * W + x0 + (ci % bw);

  // --- inpaint: multi-source BFS from the ink boundary into the bridge.
  // Each bridged pixel takes the straight RGB of the neighbor that reached it
  // first (chamfer-nearest ink pixel) and full alpha.
  const queue = new Int32Array(bridged);
  const filled = new Int32Array(bridged); // every bridged px that got paint
  let qh = 0;
  let qt = 0;
  let nf = 0;
  for (let i = 0; i < bw * bh; i++) {
    if (state[i] !== 1) continue;
    const x = i % bw;
    const y = (i / bw) | 0;
    const src = (x > 0 && binC[i - 1]) ? i - 1
      : (x < bw - 1 && binC[i + 1]) ? i + 1
        : (y > 0 && binC[i - bw]) ? i - bw
          : (y < bh - 1 && binC[i + bw]) ? i + bw : -1;
    if (src >= 0) {
      const o = toFull(i) * 4;
      const s = toFull(src) * 4;
      d[o] = d[s];
      d[o + 1] = d[s + 1];
      d[o + 2] = d[s + 2];
      d[o + 3] = 255;
      state[i] = 2;
      queue[qt++] = i;
      filled[nf++] = i;
    }
  }
  while (qh < qt) {
    const i = queue[qh++];
    const x = i % bw;
    const y = (i / bw) | 0;
    const o = toFull(i) * 4;
    const spread = (nb) => {
      const t = toFull(nb) * 4;
      d[t] = d[o];
      d[t + 1] = d[o + 1];
      d[t + 2] = d[o + 2];
      d[t + 3] = 255;
      state[nb] = 2;
      queue[qt++] = nb;
      filled[nf++] = nb;
    };
    if (x > 0 && state[i - 1] === 1) spread(i - 1);
    if (x < bw - 1 && state[i + 1] === 1) spread(i + 1);
    if (y > 0 && state[i - bw] === 1) spread(i - bw);
    if (y < bh - 1 && state[i + bw] === 1) spread(i + bw);
  }
  // Isolated bridged pixels with no ink neighbor anywhere (theoretical only —
  // a close can't add pixels away from ink) stay transparent (state 1).

  // --- feather 1: bridged pixels still exposed to background (the rounded
  // mouth of a filled pinch) get coverage-weighted alpha from the 3×3 solid
  // count, so the new outer edge is soft instead of a hard mask staircase.
  for (let k = 0; k < nf; k++) {
    const i = filled[k];
    const x = i % bw;
    const y = (i / bw) | 0;
    let exposed = false;
    let solid = 0;
    let total = 0;
    for (let dy = -1; dy <= 1; dy++) {
      const yy = y + dy;
      if (yy < 0 || yy >= bh) continue;
      for (let dx = -1; dx <= 1; dx++) {
        const xx = x + dx;
        if (xx < 0 || xx >= bw || (dx === 0 && dy === 0)) continue;
        total++;
        const nb = yy * bw + xx;
        if (binC[nb] || state[nb] === 2) solid++;
        else exposed = true;
      }
    }
    if (exposed && total) {
      // honest 3×3 coverage — a barely-attached mouth pixel goes mostly
      // transparent instead of standing up as a semi-opaque nub
      d[toFull(i) * 4 + 3] = Math.round(255 * (solid / total));
    }
  }

  // --- feather 2: original semi-transparent rim pixels (the pocket's old
  // anti-aliased edge) now sit between solid ink and solid inpaint; left
  // alone they read as a 1px see-through ring. Composite them OVER their
  // inpainted neighbor's color and make them solid. Only pixels TOUCHING an
  // inpainted one can be affected, so walking the filled list finds them all.
  for (let k = 0; k < nf; k++) {
    const i = filled[k];
    const x = i % bw;
    const y = (i / bw) | 0;
    const s = toFull(i) * 4;
    const blend = (nb) => {
      if (!binC[nb]) return;
      const o = toFull(nb) * 4;
      const a = d[o + 3];
      if (a >= 250) return;
      const m = a / 255;
      d[o] = Math.round(d[o] * m + d[s] * (1 - m));
      d[o + 1] = Math.round(d[o + 1] * m + d[s + 1] * (1 - m));
      d[o + 2] = Math.round(d[o + 2] * m + d[s + 2] * (1 - m));
      d[o + 3] = 255;
    };
    if (x > 0) blend(i - 1);
    if (x < bw - 1) blend(i + 1);
    if (y > 0) blend(i - bw);
    if (y < bh - 1) blend(i + bw);
  }

  return bridged;
}
